import './index.css'
import 'react-toastify/dist/ReactToastify.css'
import './lib/i18n'
import * as Sentry from '@sentry/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import { ToastContainer } from 'react-toastify'
import { PwaUpdatePrompt } from './components/pwa-update-prompt'
import { TooltipProvider } from './components/ui/tooltip'
import { AppRoutes } from './routes'

const queryClient = new QueryClient()
const serverUrl = import.meta.env.VITE_API_URL

function normalizeTransactionName(name: string): string {
	// Group dynamic URLs to make route popularity dashboards actionable.
	return name
		.replace(
			/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi,
			':id',
		)
		.replace(/\/\d+(?=\/|$)/g, '/:id')
}

if (import.meta.env.VITE_SENTRY_ENABLED === 'true') {
	Sentry.init({
		dsn: 'https://7c1c0be30f0a58dd63473de774614068@sentry.ripbr.com.br/3',
		environment: import.meta.env.MODE,
		// Setting this option to true will send default PII data to Sentry.
		// For example, automatic IP address collection on events
		sendDefaultPii: true,
		integrations: [
			Sentry.replayIntegration(),
			Sentry.browserTracingIntegration(),
			Sentry.browserProfilingIntegration(),
		],
		// Session Replay
		replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
		replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
		tracesSampleRate: 0.1,
		profileSessionSampleRate: 0.1,
		profileLifecycle: 'trace',
		// Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
		tracePropagationTargets: ['localhost', serverUrl],
		beforeSendTransaction(event) {
			if (event.transaction) {
				event.transaction = normalizeTransactionName(event.transaction)
			}

			return event
		},
	})
}

createRoot(document.getElementById('root')!).render(
	<StrictMode>
		<BrowserRouter>
			<QueryClientProvider client={queryClient}>
				<TooltipProvider>
					<AppRoutes />
					<PwaUpdatePrompt />
					<ToastContainer
						autoClose={2000}
						closeOnClick={false}
						draggable
						hideProgressBar={false}
						newestOnTop={false}
						pauseOnFocusLoss
						pauseOnHover={false}
						position="top-right"
						rtl={false}
						theme="light"
					/>
				</TooltipProvider>
			</QueryClientProvider>
		</BrowserRouter>
	</StrictMode>,
)
